import { entries } from 'lodash'
import { useEffect, useState } from 'react'
import IFormValues from './IFormValues'
import { useFieldValidators } from './useFieldValidators'
import { IOrganizationsItem } from '../interfaces/IOrganizationsItem'

export type IFormValue = string | IOrganizationsItem
export type CompareValues = (prevValue: IFormValue, currentValue: IFormValue) => boolean

type ValidatorTemplate<TName, TReturnValue> = (
  name: TName,
  callbackBusy?: () => void,
  callbackSuccess?: () => void,
) => TReturnValue

export type Validator = ValidatorTemplate<IFormValue, Promise<string>>
export type Validate = ValidatorTemplate<string, (currentValue: IFormValue) => Promise<string>>

export type FieldInit = {
  [key: string]: {
    value: IFormValue
    validator: Validator
    compare: CompareValues
  }
}

type FieldTool = {
  [key: string]: {
    value: IFormValue
    validator: Validator
    compare: CompareValues
    error: string
  }
}

export const useValidator = <InitialFormData extends IFormValues>(defaultValues: InitialFormData) => {
  const [fields, fieldsChange] = useState<FieldTool>({})
  const { addValidators } = useFieldValidators()

  useEffect(() => {
    const newFields: FieldTool = {}
    const defaultValuesWithValidators: FieldInit = addValidators<InitialFormData>(defaultValues)

    entries(defaultValuesWithValidators).forEach(([key, value]) => {
      newFields[key] = {
        ...value,
        error: '',
      }
    })

    fieldsChange(newFields)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validate =
    (name: string, callbackBusy?: () => void, callbackSuccess?: () => void) => async (currentValue: IFormValue) => {
      if (fields[name]) {
        const { value, validator, compare, error } = fields[name]

        if (compare(value, currentValue)) {
          const newError: string = await validator(currentValue, callbackBusy, callbackSuccess)

          fieldsChange((prevFields: FieldTool) => ({
            ...prevFields,
            [name]: {
              ...prevFields[name],
              value: currentValue,
              error: newError,
            },
          }))

          return newError
        }

        return error
      }

      return ''
    }

  return {
    validate,
  }
}
